import React, { ChangeEvent, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import ImageUploadComponent from './ImageUploadComponent'; // Assuming your component is in the same directory
import FileStruct from '../../model/FileStruct';
import { CircularProgress } from '@mui/material';
import TranslationRequestRepository, { TranslationRequestRepositoryContract } from '../../repository/TranslationRequestRepository';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { TranslationRequest } from '../../model/Translation';
import { createData, fetchData } from '../../api/DataApi';
import { CincinnatiData } from '../../model/CincinnatiData';
import { getUserEmail } from '../../utils/DetailsUtils';
import axios from 'axios';
import { Key } from '../../model/Key';
import { useError } from '../../effects/useError';
import { serverTimestamp } from 'firebase/firestore';

type FormControlElement = HTMLInputElement | HTMLTextAreaElement;

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_ID ?? ''); // Replace with your Stripe publishable key

interface TranslationFlowProps {
    onTranslationRequestComplete: (request: TranslationRequest) => void;
}

const cardElementOptions = {
    style: {
        base: {
            fontSize: '18px', // Increase the font size
            color: '#424770',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#9e2146',
        },
    },
};

const TranslationFlow: React.FC<TranslationFlowProps> = ({ onTranslationRequestComplete }) => {
    const [fromLanguage, setFromLanguage] = useState<string>('en-US');
    const [toLanguage, setToLanguage] = useState<string>('es-MX');
    const [title, setTitle] = useState('');
    const [type, setType] = useState<string>('image');
    const [textInput, setTextInput] = useState<string>('');
    const [uploadedImages, setUploadedImages] = useState<FileStruct[]>([]);
    const [showForm, setShowForm] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [priceEstimate, setPriceEstimate] = useState<number | null>(null);
    const [translationRequest, setTranslationRequest] = useState<TranslationRequest | null>(null);
    const [currency, setCurrency] = useState<string>('USD');
    const [showPayment, setShowPayment] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [fileUploadMessage, setFileUploadMessage] = useState('');
    const { error, handleError} = useError();

    const translationRepository: TranslationRequestRepositoryContract = TranslationRequestRepository;

    // Handle language selection
    const handleFromLanguageChange = (e: ChangeEvent<FormControlElement>) => {
        setFromLanguage(e.target.value);
    };

    const handleToLanguageChange = (e: ChangeEvent<FormControlElement>) => {
        setToLanguage(e.target.value);
    };

    // Handle type selection
    const handleTypeChange = (e: ChangeEvent<FormControlElement>) => {
        setType(e.target.value);
    };

    // Handle text input for "text" type
    const handleTextInputChange = (e: ChangeEvent<FormControlElement>) => {
        setTextInput(e.target.value);
    };

    // Handle uploaded images (callback from ImageUploadComponent)
    const handleImageUpload = (files: FileStruct[]) => {
        setUploadedImages(files);
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (type === 'text') {
            await createTextBasedRequest(textInput.trim());
        } else {
            // Add image processing logic here if necessary
            await createImageBasedRequestV2();
        }
    };

    const createTextBasedRequest = async (text: string) => {
        if (text.length === 0) return;
        setIsLoading(true);
        let file: FileStruct = {
            text: textInput.trim(),
            type: 'text',
            path: '',
            extension: '',
            name: '',
            pages: 1,
        };

        try {
            let request = await translationRepository.createTranslation(
                title,
                fromLanguage,
                toLanguage,
                JSON.stringify([file]),
                'text'
            );
            setTranslationRequest(request);
            setShowForm(false);
            // Simulate API call returning price estimate
            const price = request.estimated_cost; // Example price, should be returned from the API
            setPriceEstimate(price);
            setCurrency(request.currency);
            setShowPayment(true);
        } catch (err) {
            handleError(err as Error);
        } finally {
            setIsLoading(false);
        }
    };

    const createImageBasedRequestV2 = async () => {
        if (uploadedImages.length === 0) return;
        setIsLoading(true);
        try {
            setIsLoading(false);
            let userId = await getUserEmail();
            if (userId === undefined) return;
            var currentFile = 1;
            var uploadedFiles: CincinnatiData[] = [];
            for (const file of uploadedImages) {
                setFileUploadMessage(`Uploading File: ${currentFile} ...`);
                let data = await uploadImage(userId, file);
                if (data === undefined) {
                    console.log("Unable to create data");
                    return;
                }
                console.log("Uploaded image: " + JSON.stringify(data));
                uploadedFiles.push(data);
            }
            setIsLoading(true);
            let keys: Key[] = uploadedFiles.map((item) => ({ partition_key: item.partition_key, range_key: item.range_key }));
            setFileUploadMessage('');
            let request = await translationRepository.createTranslation(
                title,
                fromLanguage,
                toLanguage,
                JSON.stringify(keys),
                'image'
            );
            setTranslationRequest(request);
            setShowForm(false);
            setIsLoading(false);
            // Simulate API call returning price estimate
            const price = request.estimated_cost; // Example price, should be returned from the API
            setPriceEstimate(price);
            setCurrency(request.currency);
            setShowPayment(true);
        } catch (err) {
            setIsLoading(false);
            setFileUploadMessage('');
            handleError(err as Error)
        }
    };

    const uploadImage = async (userId: string, file: FileStruct) => {
        let fileName = file.name.replace(/\s+/g, '');
        try {
            let data = await createData(
                `temporary`,
                `${userId}-${serverTimestamp()}-${fileName}`,
                'translationrequests',
                file.name,
                '',
                file.extension,
                file.path
            );
            return data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 409) {
                    console.log("Received a conflict which means the image might already be present")
                    return await fetchData(`TranslationWeb_temporary`, `${userId}-${serverTimestamp()}-${fileName}`);
                }
            }
        }
    }

    const onPaymentSuccess = async (id: string) => {
        if (translationRequest === undefined || translationRequest === null) return;
        setPaymentError('');
        setIsLoading(true);
        try {
            translationRequest.payment_id = id;
            let result = await TranslationRequestRepository.updateTranslation(translationRequest);
            if (result) {
                onTranslationRequestComplete(translationRequest);
            } else {
                alert("There was an error in processing your translation request. Please contact the admin.");
            }
        } catch (err) {
            handleError(err as Error)
        } finally {
            setIsLoading(false);
        }
    }

    const handleTitleChange = (e: ChangeEvent<FormControlElement>) => {
        setTitle(e.target.value);
    }

    const handlePaymentFailure = (errorMessage: string) => {
        setIsLoading(false);
        setPaymentError(errorMessage);
    }

    return (
        <div style={styles.container}>
            <h2>Translation Tool</h2>
            {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </div>
            )}
            {error && (
                <div>
                    Error: `${error.name}. Please contact an admin or try again`
                </div>
            )}
            {showForm && (
                <Form>
                    {/* Language selection */}
                    <Form.Group controlId="title">
                        <Form.Label>Translation Request Title</Form.Label>
                        <Form.Control
                            type="text"
                            value={title}
                            onChange={handleTitleChange}
                            placeholder="Enter Request Title"
                        />
                    </Form.Group>
                    <Form.Group controlId="fromLanguage">
                        <Form.Label>Translate From</Form.Label>
                        <Form.Control as="select" value={fromLanguage} onChange={handleFromLanguageChange}>
                            <option value="en-US">English (US)</option>
                            <option value="es-MX">Spanish (Mexico)</option>
                            {/* Add more language options as needed */}
                        </Form.Control>
                    </Form.Group>

                    <Form.Group controlId="toLanguage">
                        <Form.Label>Translate To</Form.Label>
                        <Form.Control as="select" value={toLanguage} onChange={handleToLanguageChange}>
                            <option value="es-MX">Spanish (Mexico)</option>
                            <option value="en-US">English (US)</option>
                            {/* Add more language options as needed */}
                        </Form.Control>
                    </Form.Group>

                    {/* Type selection */}
                    <Form.Group controlId="typeSelection">
                        <Form.Label>Type</Form.Label>
                        <Form.Control as="select" value={type} onChange={handleTypeChange}>
                            {/* <option value="text">Text</option> */}
                            <option value="image">Image</option>
                        </Form.Control>
                    </Form.Group>

                    {/* Conditionally render text input or image upload component */}
                    {type === 'text' && (
                        <Form.Group controlId="textInput">
                            <Form.Label>Enter Text</Form.Label>
                            <Form.Control
                                type="text"
                                value={textInput}
                                onChange={handleTextInputChange}
                                placeholder="Enter text to translate"
                            />
                        </Form.Group>
                    )}

                    {type === 'image' && (
                        <ImageUploadComponent
                            title="Upload Images"
                            buttonTitle="Upload"
                            onUpload={handleImageUpload}
                        />
                    )}

                    <br />
                    {/* Submit Button */}
                    <Button variant="primary" onClick={handleSubmit}>
                        Submit
                    </Button>
                </Form>
            )}

            {(fileUploadMessage.length > 0) && (
                <div>
                    <h3>{fileUploadMessage}</h3>
                    <p><CircularProgress /></p>
                </div>
            )}

            {(priceEstimate && translationRequest) && (
                <div>
                    <h3>Estimated Price: {priceEstimate} {currency}</h3>
                    <p>
                        <Form.Group>
                            <Form.Label>Translate From: {fromLanguage}</Form.Label>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Translate To: {toLanguage}</Form.Label>
                        </Form.Group>
                        <Form.Group controlId="typeSelection">
                            <Form.Label>Type</Form.Label>
                            <Form.Control as="select" value={type}>
                                <option value="text">Text</option>
                                <option value="image">Image</option>
                            </Form.Control>
                        </Form.Group>
                        {translationRequest.type === "image" && (
                            <div style={{ marginTop: '16px' }}>Files: {(JSON.parse(translationRequest.files) as Key[]).length}</div>
                        )}
                    </p>
                    <p>
                        Please continue with payment to receive the translation.
                    </p>
                </div>
            )}

            {showPayment && (
                <Elements stripe={stripePromise}>
                    <PayToTranslate onLoading={() => { setIsLoading(true) }} onSuccess={onPaymentSuccess} onFailure={handlePaymentFailure} />
                    {paymentError && (
                        <p>Error with payment: ${paymentError}</p>
                    )}
                </Elements>
            )}
        </div>
    );
};

interface PayToTranslateProps {
    onLoading: () => void;
    onSuccess: (id: string) => void;
    onFailure: (message: string) => void;
}

const PayToTranslate: React.FC<PayToTranslateProps> = ({ onLoading, onSuccess, onFailure }) => {
    const stripe = useStripe();
    const elements = useElements();

    const handlePayment = async () => {
        if (!stripe || !elements) {
            return;
        }
        onLoading();
        // Example of how you would process the payment (assumes backend integration for Stripe)
        const result = await stripe.createPaymentMethod({
            type: 'card',
            card: elements.getElement(CardElement)!,
        });

        if (result.error) {
            console.error(result.error.message);
            onFailure(result.error.message ?? "Error processing payment");
        } else {
            console.log('Payment succeeded:', result.paymentMethod);
            onSuccess(result.paymentMethod.id);
        }
    };

    return (
        <div>
            <h3>Pay to Translate</h3>
            <CardElement options={cardElementOptions} />
            <Button disabled={!stripe} style={{ marginTop: '20px', padding: '10px' }} variant="success" onClick={handlePayment}>
                Pay
            </Button>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '420px',
        margin: '0 auto',
        textAlign: 'left' as 'left',
        padding: '20px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        backgroundColor: '#fff',
    },
    submitButton: {
        marginTop: '20px',
        width: '100%',
    },
};

export default TranslationFlow;
